import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Award, Download, Search, Filter, XCircle, ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Clock, ArrowRight, Layers, ClipboardCheck } from 'lucide-react';
import type { Certificate, ModuleProgress } from '../types';
import { downloadBlob, formatDate, checkSessionExpired } from '../lib/api';
import { ModuleProgressCard } from '../components/ModuleProgressCard';
import { VitnemalsportalenImport } from '../components/VitnemalsportalenImport';
import { ExternalCredentialCard } from '../components/ExternalCredentialCard';
import { KarrierepassetOnboardingBanner } from '../components/KarrierepassetOnboardingBanner';
import { LearningSuggestionsBanner } from '../components/LearningSuggestionsBanner';
import { InterestChangeNotifications } from '../components/InterestChangeNotifications';
import { PendingAssessmentsWidget } from '../components/PendingAssessmentsWidget';

interface Exam {
  id: string;
  candidate_code: string;
  course_name: string;
  exam_title: string;
  study_plan_title: string;
  study_plan_description: string;
  ects_credits: number | null;
  organization_name: string;
  association_name: string;
  submitted_at: string;
  status: string;
  result: {
    percentage: number;
    passed: boolean;
    grade: string;
    pass_threshold: number;
  } | null;
}

interface ExamsData {
  passed_unclaimed: Exam[];
  pending: Exam[];
  failed: Exam[];
}

export function DashboardPage() {
  const navigate = useNavigate();
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [exams, setExams] = useState<ExamsData>({ passed_unclaimed: [], pending: [], failed: [] });
  const [moduleProgress, setModuleProgress] = useState<ModuleProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'expired' | 'revoked'>('all');
  const [showFailedExams, setShowFailedExams] = useState(true);
  const [showPendingExams, setShowPendingExams] = useState(true);
  const [showModuleProgress, setShowModuleProgress] = useState(true);

  useEffect(() => {
    fetchCertificates();
    fetchExams();
    fetchModuleProgress();
    // Redirect til /welcome ved første innlogging hvis profil ikke har sett onboarding ennå
    fetch('/api/candidate/profile', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (d?.success && d.profile && d.profile.seen_onboarding === false) {
          navigate('/welcome', { replace: true });
        }
      })
      .catch(() => { /* ikke kritisk */ });
  }, [navigate]);

  async function fetchCertificates() {
    try {
      const res = await fetch('/api/candidate/certificates', {
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (res.ok) {
        const data = await res.json();
        setCertificates(data.certificates || []);
      }
    } catch (error) {
      console.error('Failed to fetch certificates:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchExams() {
    try {
      const res = await fetch('/api/candidate/exams', {
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (res.ok) {
        const data = await res.json();
        setExams(data.exams || { passed_unclaimed: [], pending: [], failed: [] });
      }
    } catch (error) {
      console.error('Failed to fetch exams:', error);
    }
  }

  async function fetchModuleProgress() {
    try {
      const res = await fetch('/api/certificates/modules/progress', {
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (res.ok) {
        const data = await res.json();
        if (data.success && data.progress) {
          setModuleProgress(data.progress);
        }
      }
    } catch (error) {
      console.error('Failed to fetch module progress:', error);
    }
  }

  async function handleClaimFullCertificate(studyPlanId: string) {
    try {
      const res = await fetch(`/api/certificates/modules/claim-full`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ study_plan_id: studyPlanId })
      });

      await checkSessionExpired(res);

      if (res.ok) {
        // Refresh data
        fetchCertificates();
        fetchModuleProgress();
      } else {
        const data = await res.json();
        alert(data.error || 'Kunne ikke hente samlet attest');
      }
    } catch (error) {
      console.error('Failed to claim full certificate:', error);
      alert('En feil oppstod');
    }
  }

  async function handleClaimCertificate(candidateCode: string) {
    try {
      const res = await fetch(`/api/candidate/claim/${candidateCode}`, {
        method: 'POST',
        credentials: 'include'
      });

      await checkSessionExpired(res);

      if (res.ok) {
        // Refresh data
        await Promise.all([fetchCertificates(), fetchExams()]);
      } else {
        const data = await res.json();
        alert(data.error || 'Kunne ikke hente attest');
      }
    } catch (error) {
      console.error('Claim error:', error);
      alert('Kunne ikke hente attest');
    }
  }

  async function handleDownloadPDF(cert: Certificate) {
    try {
      // Download signed PDF from the PDF endpoint
      const res = await fetch(`/api/candidate/certificates/${cert.id}/pdf`, {
        credentials: 'include',
      });

      if (!res.ok) {
        throw new Error('Kunne ikke laste ned PDF');
      }

      const blob = await res.blob();
      downloadBlob(blob, `${cert.certificate_number}.pdf`);
    } catch (error) {
      console.error('Download error:', error);
      alert('Kunne ikke laste ned PDF');
    }
  }

  const filteredCerts = certificates.filter((cert) => {
    // Search filter
    const matchesSearch = cert.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cert.certificate_number.toLowerCase().includes(searchTerm.toLowerCase());

    // Status filter. 'all' skjuler revoked — den må velges eksplisitt for å vises,
    // fordi tilbakekalte attester ellers ser likeverdige ut med aktive i grid-visningen.
    let matchesStatus = true;
    if (filterStatus === 'all') {
      matchesStatus = cert.status !== 'revoked';
    } else if (filterStatus === 'active') {
      matchesStatus = cert.status === 'active' && (!cert.valid_until || new Date(cert.valid_until) > new Date());
    } else if (filterStatus === 'expired') {
      matchesStatus = !!(cert.valid_until && new Date(cert.valid_until) < new Date());
    } else if (filterStatus === 'revoked') {
      matchesStatus = cert.status === 'revoked';
    }

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-kf-orange mx-auto mb-4"></div>
        <p className="text-kf-gray-600">Laster attester...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-kf-gray-900">Mine Kompetanseattester</h1>
          <p className="text-kf-gray-600 mt-1">
            {certificates.length} attest(er)
            {exams.passed_unclaimed.length > 0 && ` • ${exams.passed_unclaimed.length} klar til henting`}
            {exams.pending.length > 0 && ` • ${exams.pending.length} pågående`}
          </p>
        </div>
      </div>

      {/* Karrierepasset-tilkobling oppfordring (kun hvis bruker har KP-konto + ikke linket) */}
      <InterestChangeNotifications />
      <LearningSuggestionsBanner />
      <KarrierepassetOnboardingBanner />

      {/* Pågående kompositt-vurderinger (skjules stille når 0) */}
      <PendingAssessmentsWidget />

      {/* Mine vurderinger — snarvei til oversikt over sensurerte vurderinger */}
      <Link
        to="/assessments"
        className="flex items-center justify-between p-4 bg-white rounded-lg border border-kf-gray-200 hover:border-kf-blue-400 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-kf-blue-100 rounded-lg">
            <ClipboardCheck className="w-5 h-5 text-kf-blue-700" />
          </div>
          <div>
            <h3 className="font-semibold text-kf-gray-900 group-hover:text-kf-blue-700">Mine vurderinger</h3>
            <p className="text-sm text-kf-gray-600">
              Se detaljer, karakter, sensor-navn og begrunnelse for ferdige prøver og prosjekter
            </p>
          </div>
        </div>
        <ArrowRight className="w-5 h-5 text-kf-gray-400 group-hover:text-kf-blue-600" />
      </Link>

      {/* Module Progress Section */}
      {moduleProgress.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-kf-gray-900 flex items-center gap-2">
              <Layers className="w-6 h-6 text-kf-primary" />
              Modulbaserte studieplaner
            </h2>
            <button
              onClick={() => setShowModuleProgress(!showModuleProgress)}
              className="text-sm text-kf-gray-600 hover:text-kf-primary flex items-center gap-1"
            >
              {showModuleProgress ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Skjul
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Vis
                </>
              )}
            </button>
          </div>

          {showModuleProgress && (
            <div className="space-y-4">
              {moduleProgress.map((progress) => (
                <ModuleProgressCard
                  key={progress.study_plan_id}
                  progress={progress}
                  onClaimFullCertificate={
                    progress.can_claim_full_certificate
                      ? () => handleClaimFullCertificate(progress.study_plan_id)
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Search and Filters */}
      <div className="card">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-kf-gray-400" />
            <input
              type="text"
              placeholder="Søk etter tittel eller attestnummer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-kf-gray-400" />
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as any)}
              className="input"
            >
              <option value="all">Alle</option>
              <option value="active">Aktive</option>
              <option value="expired">Utgåtte</option>
              <option value="revoked">Trukket tilbake</option>
            </select>
          </div>
        </div>
      </div>

      {/* Certificates Grid – delt i to seksjoner: egne attester og eksternt importerte */}
      {(() => {
        // Kun eksplisitte eksterne kilder havner i "Importert fra Vitnemålsportalen"-seksjonen.
        // Gamle/interne external_source-verdier (f.eks. 'assessment_plan' fra v6.8) regnes som
        // egen attest — ellers ville interne prosjekt-attester feilaktig vises som VP-vitnemål.
        const EKSTERNE_KILDER: ReadonlySet<string> = new Set(['vitnemalsportalen']);
        const isExternal = (c: { external_source?: string | null }) =>
          !!c.external_source && EKSTERNE_KILDER.has(c.external_source);

        const ownCerts = filteredCerts.filter((c) => !isExternal(c));
        // Skjul sub-emner fra hovedlisten — de er inkludert i grad-kortenes courses[]-visning
        // og skulle ellers vist opp som "duplikater" (22 sub-emner for BA + MBA hos Kent).
        // Sub-emner kan ses ved å ekspandere grad-kortet via ExternalCredentialDetailPage.
        const importedCerts = filteredCerts.filter(
          (c) => isExternal(c) && !c.parent_external_id
        );
        // Antall sub-emner som er "innbakt" i grader (vises som badge på UI)
        const nestedSubCount = filteredCerts.filter(
          (c) => isExternal(c) && !!c.parent_external_id
        ).length;

        if (filteredCerts.length === 0) {
          return (
            <div className="card text-center py-12">
              <Award className="w-16 h-16 text-kf-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-kf-gray-900 mb-2">
                {searchTerm || filterStatus !== 'all' ? 'Ingen treff' : 'Ingen attester ennå'}
              </h3>
              <p className="text-kf-gray-600">
                {searchTerm || filterStatus !== 'all'
                  ? 'Prøv å endre søk eller filter'
                  : 'Dine kompetanseattester vil vises her når de er utstedt'}
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <div className="mt-6 max-w-xl mx-auto">
                  <VitnemalsportalenImport importedCount={0} />
                </div>
              )}
            </div>
          );
        }

        return (
          <div className="space-y-8">
            {/* Egne kompetanseattester */}
            {ownCerts.length > 0 && (
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <Award className="w-5 h-5 text-kf-primary" />
                  <h2 className="text-lg font-bold text-kf-gray-900">
                    Kompetanseattester ({ownCerts.length})
                  </h2>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {ownCerts.map((cert) => (
                    <CertificateCard key={cert.id} certificate={cert} onDownload={handleDownloadPDF} />
                  ))}
                </div>
              </div>
            )}

            {/* Importerte vitnemål (Vitnemålsportalen) */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-kf-blue-600" />
                  <h2 className="text-lg font-bold text-kf-gray-900">
                    Importert fra Vitnemålsportalen
                    {importedCerts.length > 0 && (
                      <span>
                        {' '}({importedCerts.length}
                        {nestedSubCount > 0 && (
                          <span className="text-sm font-normal text-kf-gray-600">
                            {' '}+ {nestedSubCount} emner
                          </span>
                        )})
                      </span>
                    )}
                  </h2>
                </div>
                {importedCerts.length >= 2 && (
                  <button
                    type="button"
                    onClick={async () => {
                      const totalToDelete = importedCerts.length + nestedSubCount;
                      if (!confirm(`Fjerne alle ${totalToDelete} vitnemål fra lommeboken (${importedCerts.length} grader/vitnemål${nestedSubCount > 0 ? ` inkl. ${nestedSubCount} emner` : ''})? Du kan importere på nytt senere.`)) return;
                      try {
                        const res = await fetch('/api/candidate/vp/imports', {
                          method: 'DELETE',
                          credentials: 'include'
                        });
                        const data = await res.json();
                        if (!res.ok || !data.success) throw new Error(data.error || 'Sletting feilet');
                        setCertificates((prev) => prev.filter((c) => c.external_source !== 'vitnemalsportalen'));
                      } catch (e) {
                        alert(e instanceof Error ? e.message : 'Kunne ikke slette vitnemålene');
                      }
                    }}
                    className="text-xs text-red-600 hover:text-red-800 hover:bg-red-50 px-3 py-1.5 rounded-md font-medium border border-red-200"
                  >
                    Fjern alle vitnemål
                  </button>
                )}
              </div>
              {importedCerts.length > 0 ? (
                <>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {importedCerts.map((cert) => (
                      <ExternalCredentialCard
                        key={cert.id}
                        certificate={cert}
                        onDeleted={(id) => setCertificates((prev) => prev.filter((c) => c.id !== id))}
                      />
                    ))}
                  </div>
                  <VitnemalsportalenImport importedCount={importedCerts.length} />
                </>
              ) : (
                <VitnemalsportalenImport importedCount={0} />
              )}
            </div>
          </div>
        );
      })()}

      {/* Passed Unclaimed Exams Section - Ready to claim */}
      {exams.passed_unclaimed.length > 0 && (
        <div className="mt-8 border-t pt-8">
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle className="w-5 h-5 text-kf-green" />
            <h2 className="text-xl font-bold text-kf-gray-900">
              Beståtte eksamener - klar til henting ({exams.passed_unclaimed.length})
            </h2>
          </div>
          <div className="space-y-4">
            {exams.passed_unclaimed.map((exam) => (
              <PassedExamCard
                key={exam.id}
                exam={exam}
                onClaim={handleClaimCertificate}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pending Exams Section */}
      {exams.pending.length > 0 && (
        <div className="mt-8 border-t pt-8">
          <button
            onClick={() => setShowPendingExams(!showPendingExams)}
            className="flex items-center gap-2 text-kf-gray-700 hover:text-kf-gray-900 mb-4"
          >
            <Clock className="w-5 h-5 text-kf-blue-500" />
            <h2 className="text-xl font-bold">Pågående eksamener ({exams.pending.length})</h2>
            {showPendingExams ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          {showPendingExams && (
            <div className="space-y-4">
              {exams.pending.map((exam) => (
                <PendingExamCard key={exam.id} exam={exam} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Failed Exams Section */}
      {exams.failed.length > 0 && (
        <div className="mt-8 border-t pt-8">
          <button
            onClick={() => setShowFailedExams(!showFailedExams)}
            className="flex items-center gap-2 text-kf-gray-700 hover:text-kf-gray-900 mb-4"
          >
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <h2 className="text-xl font-bold">Ikke beståtte eksamener ({exams.failed.length})</h2>
            {showFailedExams ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}
          </button>

          {showFailedExams && (
            <div className="space-y-4">
              {exams.failed.map((exam) => (
                <FailedExamCard key={exam.id} exam={exam} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CertificateCard({
  certificate,
  onDownload
}: {
  certificate: Certificate;
  onDownload: (cert: Certificate) => void;
}) {
  const isExpired = certificate.valid_until && new Date(certificate.valid_until) < new Date();
  const isRevoked = certificate.status === 'revoked';
  // #129 Samle-attest-visning
  const isAggregate = certificate.is_aggregated === true;
  const childCount = certificate.aggregate_child_ids?.length ?? 0;
  const weightedScore = certificate.aggregate_weighted_score;
  const isInvalidated = !!certificate.aggregate_invalidated_at;
  const isNew = isAggregate && !certificate.aggregate_notified_at && !isRevoked && !isInvalidated;

  const borderClass = isAggregate
    ? 'border-l-4 border-l-kf-primary ring-1 ring-kf-primary/20'
    : '';

  return (
    <div className={`card hover:shadow-lg transition-shadow ${borderClass} relative`}>
      {isNew && (
        <div className="absolute -top-2 -right-2 bg-kf-orange text-white text-xs font-bold px-2 py-1 rounded-full shadow-md">
          NY
        </div>
      )}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <div className="text-xs text-kf-gray-500">{certificate.certificate_number}</div>
            {isAggregate && (
              <span className="text-xs font-semibold text-kf-primary bg-kf-primary/10 px-2 py-0.5 rounded">
                Samle-attest
              </span>
            )}
          </div>
          <h3 className="font-bold text-kf-gray-900 line-clamp-2">{certificate.title}</h3>
        </div>
        <div
          className={`px-2 py-1 rounded text-xs font-medium ${
            isRevoked || isInvalidated
              ? 'bg-kf-error-100 text-kf-error-700'
              : isExpired
              ? 'bg-amber-100 text-amber-700'
              : 'bg-kf-green/10 text-kf-green'
          }`}
        >
          {isInvalidated ? 'Ugyldig' : isRevoked ? 'Trukket' : isExpired ? 'Utgått' : 'Aktiv'}
        </div>
      </div>

      <div className="text-sm text-kf-gray-600 mb-4 space-y-1">
        <div>Utstedt: {formatDate(certificate.issue_date)}</div>
        {certificate.ects_credits && (
          <div>{certificate.ects_credits} kompetansepoeng (ECTS){isAggregate && childCount > 0 ? ` · ${childCount} komponenter` : ''}</div>
        )}
        {isAggregate && weightedScore != null && (
          <div className="text-kf-primary font-medium">Vektet snitt: {Number(weightedScore).toFixed(1)} %</div>
        )}
        {certificate.valid_until && (
          <div>
            Gyldig til: {formatDate(certificate.valid_until)}
            {isExpired && ' ⚠️'}
          </div>
        )}
        {isInvalidated && certificate.aggregate_invalidation_reason && (
          <div className="text-xs text-kf-error-700 italic mt-2 border-t pt-2">
            {certificate.aggregate_invalidation_reason}
          </div>
        )}
      </div>

      <div className="flex gap-2">
        <Link
          to={`/certificates/${certificate.id}`}
          className="btn-primary flex-1 text-center text-sm py-2"
        >
          Vis
        </Link>
        <button
          onClick={() => onDownload(certificate)}
          className="btn-secondary p-2"
          title="Last ned PDF"
        >
          <Download className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function PassedExamCard({ exam, onClaim }: { exam: Exam; onClaim: (candidateCode: string) => void }) {
  const [claiming, setClaiming] = useState(false);

  const handleClaim = async () => {
    setClaiming(true);
    await onClaim(exam.candidate_code);
    setClaiming(false);
  };

  return (
    <div className="card border-l-4 border-l-kf-green bg-kf-green/5">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="font-bold text-kf-gray-900">{exam.study_plan_title}</h3>
          <p className="text-sm text-kf-gray-600">{exam.organization_name}</p>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-kf-gray-500">
            {exam.result && (
              <span className="text-kf-green font-medium">
                {Math.round(exam.result.percentage)}% - Bestått
              </span>
            )}
            {exam.ects_credits && (
              <span>{exam.ects_credits} Kompetansepoeng</span>
            )}
            {exam.submitted_at && (
              <span>Eksamen: {formatDate(exam.submitted_at)}</span>
            )}
          </div>
        </div>
        <button
          onClick={handleClaim}
          disabled={claiming}
          className="btn-primary flex items-center gap-2 ml-4"
        >
          {claiming ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Henter...
            </>
          ) : (
            <>
              <Award className="w-4 h-4" />
              Hent attest
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function PendingExamCard({ exam }: { exam: Exam }) {
  const getStatusText = (status: string) => {
    switch (status) {
      case 'registered': return 'Registrert';
      case 'started': return 'Startet';
      case 'submitted': return 'Levert - venter på sensur';
      default: return 'Pågående';
    }
  };

  return (
    <div className="card border-l-4 border-l-kf-blue-400">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h3 className="font-bold text-kf-gray-900">{exam.study_plan_title}</h3>
          <p className="text-sm text-kf-gray-600">{exam.organization_name}</p>
          <div className="mt-2 flex flex-wrap gap-3 text-sm text-kf-gray-500">
            <span className="text-kf-blue-600 font-medium flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {getStatusText(exam.status)}
            </span>
            {exam.ects_credits && (
              <span>{exam.ects_credits} Kompetansepoeng</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FailedExamCard({ exam }: { exam: Exam }) {
  const [expanded, setExpanded] = useState(false);

  const getGradeColor = (grade: string) => {
    if (grade === 'F') return 'text-red-600';
    if (grade === 'E') return 'text-kf-orange-600';
    return 'text-amber-600';
  };

  if (!exam.result) return null;

  return (
    <div className="card border-l-4 border-l-amber-400">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left"
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <h3 className="font-bold text-kf-gray-900">{exam.study_plan_title}</h3>
            <p className="text-sm text-kf-gray-600">{exam.organization_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <span className={`font-bold ${getGradeColor(exam.result.grade)}`}>
                {Math.round(exam.result.percentage)}%
              </span>
              <span className="text-kf-gray-500 ml-1">/ {exam.result.pass_threshold}%</span>
            </div>
            {expanded ? (
              <ChevronUp className="w-5 h-5 text-kf-gray-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-kf-gray-400" />
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-kf-gray-100">
          <div className="bg-kf-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-kf-gray-500">Resultat</p>
                <p className="font-bold text-lg">
                  <span className={getGradeColor(exam.result.grade)}>
                    {Math.round(exam.result.percentage)}%
                  </span>
                  <span className="text-kf-gray-400 text-sm ml-2">
                    (Karakter: {exam.result.grade})
                  </span>
                </p>
              </div>
              <div>
                <p className="text-sm text-kf-gray-500">Bestått-grense</p>
                <p className="font-bold text-lg">{exam.result.pass_threshold}%</p>
              </div>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-kf-gray-500">Kurs:</span>
                <span className="text-kf-gray-900">{exam.course_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-kf-gray-500">Eksamen:</span>
                <span className="text-kf-gray-900">{exam.exam_title}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-kf-gray-500">Organisasjon:</span>
                <span className="text-kf-gray-900">{exam.organization_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-kf-gray-500">Studieforbund:</span>
                <span className="text-kf-gray-900">{exam.association_name}</span>
              </div>
              {exam.ects_credits && (
                <div className="flex justify-between">
                  <span className="text-kf-gray-500">Kompetansepoeng:</span>
                  <span className="text-kf-gray-900">{exam.ects_credits}</span>
                </div>
              )}
              {exam.submitted_at && (
                <div className="flex justify-between">
                  <span className="text-kf-gray-500">Eksamensdato:</span>
                  <span className="text-kf-gray-900">{formatDate(exam.submitted_at)}</span>
                </div>
              )}
            </div>

            <div className="mt-4 p-3 bg-amber-50 rounded border border-amber-200">
              <div className="flex items-start gap-2">
                <XCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800">Eksamen ikke bestått</p>
                  <p className="text-sm text-amber-700 mt-1">
                    Du oppnådde {Math.round(exam.result.percentage)}%, men trengte minst {exam.result.pass_threshold}% for å bestå.
                    Kontakt kursarrangøren for informasjon om mulighet til å ta eksamen på nytt.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
